import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Scale } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { currentShift } from "@/lib/shift";
import { openCustomerDisplay, openDrawerManual, postDisplay, touchActive } from "@/lib/tpv";
import { getCurrent, openTerminal } from "@/lib/terminals";
import { round2, vals } from "@/lib/format";
import useScannerInput from "@/lib/useScannerInput";
import useTicket from "@/components/terminal/useTicket";
import StartScreen from "@/components/terminal/StartScreen";
import SaleScreen from "@/components/terminal/SaleScreen";
import TerminalPicker from "@/components/terminal/TerminalPicker";
import TextDialog from "@/components/terminal/TextDialog";
import CheckoutDialog, { saleFrom } from "@/components/pos/CheckoutDialog";
import SplitDialog from "@/components/pos/SplitDialog";
import CustomerPicker from "@/components/customers/CustomerPicker";
import HeldDialog from "@/components/tpv/HeldDialog";
import ShiftDialog from "@/components/tpv/ShiftDialog";
import DrawerKick from "@/components/tpv/DrawerKick";
import useManagerGate from "@/components/tpv/ManagerGate";
import WeighPanel from "@/components/scale/WeighPanel";
import Receipt from "@/components/Receipt";

export default function Terminal() {
  const { t, view, staff, setStaff, dispatch } = useApp();
  const nav = useNavigate();
  const tk = useTicket();
  const [term, setTerm] = useState(getCurrent);
  const [picker, setPicker] = useState(() => !getCurrent());
  const [screen, setScreen] = useState("start");
  const [buf, setBuf] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [mode, setMode] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [shiftTab, setShiftTab] = useState(null);
  const [gate, gateDialog] = useManagerGate();
  const shift = currentShift(view);

  useEffect(() => {
    const h = (e) => setShiftTab(e.detail?.tab || "x");
    const root = document.documentElement;
    root.classList.remove("avalon-ui");
    root.classList.toggle("touch-mode", touchActive());
    window.addEventListener("tpv:shift", h);
    return () => window.removeEventListener("tpv:shift", h);
  }, []);
  useEffect(() => {
    if (receipt) return;
    postDisplay({ type: "cart", currency: view.shop?.currency, shop: view.shop?.name, total: tk.totals.total, discount: 0,
      lines: tk.cart.map((l) => ({ name: l.name, qty: l.qty, kg: !!l.ppk, amount: round2(l.price * l.qty) })) });
  }, [tk.cart, tk.totals.total, receipt, view.shop]);

  const takeQty = () => { const n = Number(buf); setBuf(""); return n > 0 ? n : 1; };
  const scan = (code, qty = 1) => {
    const name = tk.resolve(code, qty);
    if (name) { toast.success(name); setScreen("sale"); return; }
    toast.error(`${t("not_found_barcode")}: ${code}`);
  };
  useScannerInput(scan, !mode && !dlg && !receipt && !tk.weighing && !picker && shiftTab === null);
  const onKey = (k) => {
    if (k === "clear") return setBuf("");
    if (k === "az") return setDlg("search");
    if (k === "enter") {
      if (!buf) return undefined;
      setBuf("");
      if (tk.resolve(buf)) { setScreen("sale"); return undefined; }
      if (screen === "sale" && tk.sel) return tk.setQty(tk.sel, Number(buf));
      return toast.error(`${t("not_found_barcode")}: ${buf}`);
    }
    if (k === "." && buf.includes(".")) return undefined;
    return setBuf((b) => (b + k).slice(0, 20));
  };
  const done = (sale) => {
    postDisplay({ type: "done", total: sale.total, change: sale.given > sale.total ? round2(sale.given - sale.total) : 0, currency: view.shop?.currency, shop: view.shop?.name });
    setReceipt({ ...sale, fresh: true }); tk.reset(); setMode(null);
  };
  const pay = (m) => {
    if (!tk.cart.length) return;
    if (!shift) { toast.error(t("no_shift_banner")); setShiftTab("x"); return; }
    if (m === "split") { setMode("split"); return; }
    if (m !== "cash" && m !== "card") { setMode(m === "advanced" ? "cash" : "credit"); return; }
    const customer = tk.customerId ? view.customers[tk.customerId] : null;
    const sale = saleFrom({ cart: tk.cart, totals: tk.totals, mode: m, paid: tk.totals.total, given: m === "cash" ? tk.totals.total : null, customer });
    if (!dispatch("sale.create", sale)) return;
    toast.success(t("sale_saved"));
    done({ ...sale, staff_name: staff.name, created_at: new Date().toISOString(), balance_after: customer ? customer.balance : null });
  };
  const line = tk.cart.find((l) => l.product_id === tk.sel);
  const needLine = (fn) => () => (line ? fn(line) : toast.error(t("tm_select_line")));
  const lastSale = () => vals(view.sales).sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];
  const act = {
    pickTerm: () => setPicker(true),
    customer: () => setDlg("customer"),
    newSale: () => setScreen("sale"),
    menu: () => setScreen("start"),
    lastTicket: () => { const s = lastSale(); if (s) setReceipt(s); else toast.error(t("tm_no_ticket")); },
    proforma: () => tk.cart.length && setReceipt({ receipt_no: "PROFORMA", items: tk.cart, subtotal: tk.totals.subtotal, discount: 0, total: tk.totals.total, paid: 0, payment: "proforma", staff_name: staff.name, created_at: new Date().toISOString() }),
    article: () => nav("/price-check"),
    display: openCustomerDisplay,
    lock: () => setStaff(null),
    exit: () => nav("/tpv"),
    accounts: () => setDlg("held"),
    recall: (h) => { tk.recall(h); setScreen("sale"); },
    park: () => { if (tk.hold()) { toast.success(t("tm_parked")); setScreen("start"); } },
    funds: () => setShiftTab(shift ? "in" : "x"),
    services: () => nav("/sales"),
    arqueo: () => setShiftTab("x"),
    drawer: openDrawerManual,
    product: (p) => tk.add(p, takeQty()),
    pay,
    note: needLine(() => setDlg("note")),
    voidLine: needLine((l) => tk.setQty(l.product_id, 0)),
    reduce: needLine((l) => tk.setQty(l.product_id, l.ppk ? 0 : l.qty - 1)),
    linePrice: needLine((l) => {
      const x = Number(buf);
      if (!(x > 0)) { toast.error(t("tm_type_first")); return; }
      setBuf("");
      gate(() => tk.patchLine(l.product_id, l.ppk ? { ppk: x } : { base: x }));
    }),
  };
  const Screen = screen === "start" ? StartScreen : SaleScreen;
  return (
    <div className="min-h-screen bg-[#4A80B9] p-1 sm:p-2" data-testid="terminal-page">
      <div className="max-w-[1340px] mx-auto bg-[#F5F8FC] p-1 lg:h-[calc(100vh-16px)] overflow-hidden">
        <Screen term={term} tk={tk} buf={buf} q={q} onClearQ={() => setQ("")} onKey={onKey} act={act} />
      </div>
      <TerminalPicker open={picker} onClose={() => setPicker(false)} onOpen={(id) => { setTerm(openTerminal(id)); setPicker(false); }} />
      <Dialog open={dlg === "customer"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-md" data-testid="tm-customer-dialog">
          <DialogTitle className="font-heading text-xl">{t("tm_customer_id")}</DialogTitle>
          <CustomerPicker value={tk.customerId} onChange={(id) => { tk.setCustomerId(id); setDlg(null); }} />
        </DialogContent>
      </Dialog>
      <TextDialog open={dlg === "search"} title="A … Z" initial={q} onClose={() => setDlg(null)} onSave={(v) => { setQ(v); setDlg(null); setScreen("sale"); }} testid="tm-search-dialog" />
      <TextDialog open={dlg === "note"} title={t("tm_line_note")} initial={line?.note || ""} onClose={() => setDlg(null)} onSave={(v) => { tk.patchLine(tk.sel, { note: v }); setDlg(null); }} testid="tm-note-dialog" />
      <HeldDialog open={dlg === "held"} onClose={() => setDlg(null)} held={tk.held} onRecall={(h) => { act.recall(h); setDlg(null); }} onDelete={tk.removeHeld} />
      <Dialog open={!!tk.weighing} onOpenChange={(o) => !o && tk.setWeighing(null)}>
        <DialogContent className="max-w-md" data-testid="tm-weigh-dialog">
          <DialogTitle className="font-heading text-2xl flex items-center gap-2"><Scale className="w-6 h-6" />{tk.weighing?.name}</DialogTitle>
          {tk.weighing && <WeighPanel key={tk.weighing.id} product={tk.weighing} confirmLabel={t("add_to_cart")} confirmIcon={Plus}
            onConfirm={(kg, total) => { tk.addWeighed(tk.weighing, kg, total); tk.setWeighing(null); }} />}
        </DialogContent>
      </Dialog>
      <CheckoutDialog gate={gate} mode={mode === "split" ? null : mode} onClose={() => setMode(null)} cart={tk.cart} totals={tk.totals} promo={null} onDone={done} initialCustomer={tk.customerId} />
      <SplitDialog open={mode === "split"} onClose={() => setMode(null)} cart={tk.cart} totals={tk.totals} onDone={done} initialCustomer={tk.customerId} />
      <Receipt sale={receipt} onClose={() => setReceipt(null)} fresh={!!receipt?.fresh} />
      <ShiftDialog open={shiftTab !== null} initialTab={shiftTab || "x"} onClose={() => setShiftTab(null)} />
      <DrawerKick />
      {gateDialog}
    </div>
  );
}
